import React, { useState, useEffect } from 'react';
import { useRestStore } from '../../stores/useRestStore';

interface RestSettingsModalProps {
  visible: boolean;
  onClose: () => void;
}

const RestSettingsModal: React.FC<RestSettingsModalProps> = ({ visible, onClose }) => {
  const { restSettings, updateRestSettings } = useRestStore();
  
  const [dailyGoalMinutes, setDailyGoalMinutes] = useState(restSettings.dailyGoalMinutes);
  const [maxDailyMinutes, setMaxDailyMinutes] = useState(restSettings.maxDailyMinutes);
  const [reminderLevel, setReminderLevel] = useState(restSettings.reminderLevel);

  // モーダルが開かれたときに最新の値で初期化
  useEffect(() => {
    if (visible) {
      setDailyGoalMinutes(restSettings.dailyGoalMinutes);
      setMaxDailyMinutes(restSettings.maxDailyMinutes);
      setReminderLevel(restSettings.reminderLevel);
    }
  }, [visible, restSettings]);

  if (!visible) return null;

  const handleSave = () => {
    updateRestSettings({
      dailyGoalMinutes,
      maxDailyMinutes,
      reminderLevel,
    });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 glass-overlay z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="glass-panel p-6 text-white max-w-md w-full max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold mb-6">目標設定</h2>

        <div className="space-y-6">
          {/* 今日の目標 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              今日の目標作業時間
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="25"
                max="200"
                step="25"
                value={dailyGoalMinutes}
                onChange={(e) => setDailyGoalMinutes(Number(e.target.value))}
                className="flex-1 h-2 bg-white/[0.08] rounded-lg appearance-none cursor-pointer
                         [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4
                         [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full
                         [&::-webkit-slider-thumb]:bg-green-500"
              />
              <span className="text-green-400 w-20 text-right">{dailyGoalMinutes}分</span>
            </div>
            <p className="text-xs text-white/40 mt-1">
              {dailyGoalMinutes === 25 ? '1ポモドーロで十分！' :
               dailyGoalMinutes <= 50 ? '無理しないでね' :
               dailyGoalMinutes <= 100 ? 'ぼちぼちがんばろう' :
               'がんばりすぎ注意！'}
            </p>
          </div>

          {/* 心配ライン */}
          <div>
            <label className="block text-sm font-medium mb-2">
              心配になる作業時間
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="60"
                max="300"
                step="30"
                value={maxDailyMinutes}
                onChange={(e) => setMaxDailyMinutes(Number(e.target.value))}
                className="flex-1 h-2 bg-white/[0.08] rounded-lg appearance-none cursor-pointer
                         [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4
                         [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full
                         [&::-webkit-slider-thumb]:bg-orange-500"
              />
              <span className="text-orange-400 w-20 text-right">{maxDailyMinutes}分</span>
            </div>
            <p className="text-xs text-white/40 mt-1">
              {maxDailyMinutes}分を超えたら休憩を促します
            </p>
          </div>

          <div className="border-t border-white/[0.08] pt-4"></div>

          {/* 通知レベル */}
          <div>
            <label className="block text-sm font-medium mb-2">
              通知の強さ
            </label>
            <div className="space-y-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="gentle"
                  checked={reminderLevel === 'gentle'}
                  onChange={(e) => setReminderLevel(e.target.value as 'gentle' | 'off')}
                  className="mr-2"
                />
                <span className="text-sm">やさしく（デフォルト）</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="off"
                  checked={reminderLevel === 'off'}
                  onChange={(e) => setReminderLevel(e.target.value as 'gentle' | 'off')}
                  className="mr-2"
                />
                <span className="text-sm">オフ</span>
              </label>
            </div>
            <p className="text-xs text-white/40 mt-2">
              {reminderLevel === 'gentle' ? '空ちゃんがやさしく休憩を促してくれます' :
               '休憩の通知はされません'}
            </p>
          </div>
        </div>

        {/* ボタン */}
        <div className="flex gap-3 mt-8">
          <button
            onClick={onClose}
            className="flex-1 py-2 px-4 glass-btn transition-colors"
          >
            キャンセル
          </button>
          <button
            onClick={handleSave}
            className="flex-1 py-2 px-4 glass-btn-primary transition-colors"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
};

export default RestSettingsModal;